import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type {
  AdminEdition,
  AdminEditionSummary,
  EditionListResponse,
  EditionSaveBody,
  GuideOption,
  GuideScreenshots,
  ReaderEdition,
  ViewingAs
} from '@/types/releaseNotes';

export const releaseNoteKeys = {
  all: ['release-notes'] as const,
  list: (asUser?: string) => [...releaseNoteKeys.all, 'list', asUser ?? null] as const,
  edition: (key: string, asUser?: string) => [...releaseNoteKeys.all, 'edition', key, asUser ?? null] as const,
  image: (s3Key: string) => [...releaseNoteKeys.all, 'image', s3Key] as const,
  admin: () => [...releaseNoteKeys.all, 'admin'] as const,
  adminEdition: (key: string) => [...releaseNoteKeys.admin(), key] as const,
  preview: (key: string, as: string, asUserId?: string) => [...releaseNoteKeys.admin(), key, 'preview', as, asUserId ?? null] as const,
  screenshots: (area: string) => [...releaseNoteKeys.admin(), 'screenshots', area] as const,
  guides: () => [...releaseNoteKeys.admin(), 'guides'] as const
};

const lens = (asUser?: string) => (asUser ? { asUserId: asUser } : {});

/** The editions this reader can see, newest first. Empty until the first edition is approved. */
export function useReleaseNotes(asUser?: string) {
  return useQuery({
    queryKey: releaseNoteKeys.list(asUser),
    queryFn: () => api<EditionListResponse>('/release-notes', { query: lens(asUser) }),
    staleTime: 5 * 60_000
  });
}

export function useReleaseNoteEdition(key: string, asUser?: string) {
  return useQuery({
    queryKey: releaseNoteKeys.edition(key, asUser),
    queryFn: () => api<ReaderEdition>(`/release-notes/${key}`, { query: lens(asUser) }),
    enabled: Boolean(key),
    retry: false
  });
}

/** A highlight screenshot's short-lived URL. Kept a little under the API's five-minute presign. */
export function useReleaseNoteImage(s3Key: string | null | undefined) {
  return useQuery({
    queryKey: releaseNoteKeys.image(s3Key ?? ''),
    queryFn: async () => (await api<{ url: string }>(`/release-notes/images/${encodeURIComponent(s3Key ?? '')}`, { query: { json: '1' } })).url,
    enabled: Boolean(s3Key),
    staleTime: 4 * 60_000
  });
}

export function useAdminEditions() {
  return useQuery({
    queryKey: releaseNoteKeys.admin(),
    queryFn: async () => (await api<{ data: AdminEditionSummary[] }>('/release-notes/admin/editions')).data
  });
}

export function useAdminEdition(key: string) {
  return useQuery({
    queryKey: releaseNoteKeys.adminEdition(key),
    queryFn: () => api<AdminEdition>(`/release-notes/admin/editions/${key}`),
    enabled: Boolean(key)
  });
}

export type PreviewMode = 'everyone' | 'partner' | 'user';

export function useEditionPreview(key: string, as: PreviewMode, asUserId?: string, { enabled = true } = {}) {
  return useQuery({
    queryKey: releaseNoteKeys.preview(key, as, asUserId),
    queryFn: () =>
      api<{ edition: ReaderEdition | null; viewingAs?: ViewingAs }>(`/release-notes/admin/editions/${key}/preview`, {
        query: { as, ...(asUserId ? { asUserId } : {}) }
      }),
    enabled: enabled && Boolean(key) && (as !== 'user' || Boolean(asUserId))
  });
}

function useEditionMutation<T>(key: string, request: (body: T) => Promise<AdminEdition>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: edition => {
      qc.setQueryData(releaseNoteKeys.adminEdition(key), edition);
      qc.invalidateQueries({ queryKey: releaseNoteKeys.all });
    }
  });
}

export function useSaveEdition(key: string) {
  return useEditionMutation<EditionSaveBody>(key, body => api<AdminEdition>(`/release-notes/admin/editions/${key}`, { method: 'PUT', body }));
}

export function useApproveEdition(key: string) {
  return useEditionMutation<void>(key, () => api<AdminEdition>(`/release-notes/admin/editions/${key}/approve`, { method: 'POST' }));
}

export function useWithdrawEdition(key: string) {
  return useEditionMutation<void>(key, () => api<AdminEdition>(`/release-notes/admin/editions/${key}/withdraw`, { method: 'POST' }));
}

export function useUploadHighlightImage() {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api<{ s3Key: string }>('/release-notes/admin/images', { method: 'POST', body: form });
    }
  });
}

export function useGuideScreenshots(area: string) {
  return useQuery({
    queryKey: releaseNoteKeys.screenshots(area),
    queryFn: async () => (await api<{ data: GuideScreenshots[] }>('/release-notes/admin/screenshots', { query: { area } })).data,
    enabled: Boolean(area),
    staleTime: 5 * 60_000
  });
}

export function useGuideOptions() {
  return useQuery({
    queryKey: releaseNoteKeys.guides(),
    queryFn: async () => (await api<{ data: GuideOption[] }>('/release-notes/admin/guides')).data,
    staleTime: 5 * 60_000
  });
}
